import { useState } from 'react';

import { useTranslations } from 'use-intl';

import type { HandsFreeOverlaysValue } from './project-usability-handsfree-content.types';
import ProjectUsabilityHandsFreeOnboarding from './project-usability-handsfree-onboarding';
import ProjectUsabilityHandsFreeWelcomeModal from './project-usability-handsfree-welcome-modal';
import ProjectUsabilityResponseSaving from './project-usability-response-saving';
import ProjectUsabilityResponseWrappingUp from './project-usability-response-wrapping-up';
import ProjectUsabilitySpeechPrompt from './project-usability-speech-prompt';
import ProjectUsabilityTimeoutCountdown from './project-usability-timeout-countdown';
import ProjectUsabilityTimeoutOverlay from './project-usability-timeout-overlay';
import ProjectUsabilityTranscriptErrorPrompt from './project-usability-transcript-error-prompt';

export default function ProjectUsabilityHandsFreeOverlays({
  projectId,
  recordingError,
  showSubmitted,
  isRecording,
  isMobileMode,
  isPausedByTimeout,
  currentRemainingTime,
  showTimeoutOverlay,
  onResumeTimeout,
  isUserPaused,
  onResumeUserPause,
  isScreenShareMuted,
  showSpeechPrompt,
  isOnboardingActive,
  isSpeechFadingOut,
  onSpeechPromptClose,
  showTranscriptError,
  onTranscriptErrorClose,
  ttsHasDrained,
  isFinalizingRecording,
  hasUrlOrTask,
  isUploadStalled,
  onRetryUpload,
  uploadProgress,
  currentOnboardingStep,
  onOnboardingNext,
  onOnboardingEnd,
  showWelcomePrompt,
  orgSettings,
  onStartRecording,
  hasRequestedStart,
  isRequestingPermission,
}: HandsFreeOverlaysValue) {
  const t = useTranslations('Usability');

  const [finalizationStarted, setFinalizationStarted] = useState(false);

  if (isFinalizingRecording && !finalizationStarted) {
    setFinalizationStarted(true);
  }

  return (
    <>
      {recordingError && (
        <div className='fixed top-2 left-2 z-50 rounded-sm bg-yellow-600 px-3 py-1 text-xs text-white shadow-sm'>
          {recordingError}
        </div>
      )}

      {!showSubmitted && (
        <>
          <ProjectUsabilityTimeoutCountdown
            remainingTime={currentRemainingTime}
            isVisible={isRecording && !isPausedByTimeout}
          />

          <ProjectUsabilityTimeoutOverlay
            isVisible={showTimeoutOverlay}
            onResume={onResumeTimeout}
          />

          <ProjectUsabilityTimeoutOverlay
            isVisible={isUserPaused && !showTimeoutOverlay}
            onResume={onResumeUserPause}
            title={t('sessionPausedTitle')}
            message={t('handsFreeSessionPausedMessage')}
            buttonText={t('handsFreeSessionPausedButton')}
          />

          <ProjectUsabilityTimeoutOverlay
            isVisible={isScreenShareMuted}
            onResume={() => window.location.reload()}
            title={t('screenSharePausedTitle')}
            message={t('screenSharePausedMessage')}
            buttonText={t('screenSharePausedButton')}
          />

          <ProjectUsabilitySpeechPrompt
            isVisible={showSpeechPrompt && !isOnboardingActive}
            isFadingOut={isSpeechFadingOut}
            isSidebarOpen={false}
            onClose={onSpeechPromptClose}
            isHandsFree
          />

          <ProjectUsabilityTranscriptErrorPrompt
            isVisible={showTranscriptError}
            onClose={onTranscriptErrorClose}
          />
        </>
      )}

      {ttsHasDrained &&
        !isFinalizingRecording &&
        !finalizationStarted &&
        hasUrlOrTask && <ProjectUsabilityResponseWrappingUp />}

      {ttsHasDrained && isFinalizingRecording && (
        <ProjectUsabilityResponseSaving
          uploadProgress={uploadProgress}
          isStalled={isUploadStalled}
          onRetry={onRetryUpload}
        />
      )}

      {!showSubmitted && isOnboardingActive && !isMobileMode && (
        <ProjectUsabilityHandsFreeOnboarding
          key={currentOnboardingStep}
          stepIndex={currentOnboardingStep}
          onNext={onOnboardingNext}
          onEnd={onOnboardingEnd}
        />
      )}

      {showWelcomePrompt && !isRecording && (
        <ProjectUsabilityHandsFreeWelcomeModal
          projectId={projectId}
          orgSettings={orgSettings}
          onContinue={onStartRecording}
          isLoading={hasRequestedStart || isRequestingPermission}
          showCameraSetup={!isMobileMode}
        />
      )}
    </>
  );
}
